/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format
/** Map Importer
 */

import Events from './../core/highed.events';
import { getLetterFromIndex, merge, clean, generateRandomColors, isNull } from './../core/highcharts-editor';
import chartTypes from './../meta/highed.meta.charttype';
import assignDefaults from './../meta/highed.meta.assign.defaults';
import { getSeriesType } from './../core/highed.chart-helpers';
import { cloneDeep } from 'lodash';

export default class DataImporterHelper {
  constructor(chartPreview) {
    this.events = Events();
    this.container = null;
    this.chartPreview = chartPreview;
    this.parseWizardData = this.parseWizardData.bind(this);

    this.selects = [
      {
        // TODO: Take these from assign data
        name: 'Disabled'
      },
      {
        name: 'Categories',
        value: 'labels',
        colors: {
          light: 'rgba(66, 200, 192, 0.2)',
          dark: 'rgb(66, 200, 192)'
        },
        mandatory: true
      },
      {
        name: 'Values',
        value: 'values',
        mandatory: true,
        colors: {
          light: 'rgba(145, 151, 229, 0.2)',
          dark: 'rgb(145, 151, 229)'
        },
        multiple: true
      }
    ];
    this.data = null;
    this.toNextPage = null;
    this.mapData = null;
    this.parsedData = null;

    this.seriesPrefix = '';
    this.seriesSuffix = '';
    this.rowsSeries = [];
    this.seriesCount = 0;
    this.seriesLeeway = 2;
    this.assignOptions = {};
    this.dataParentContainer = document.querySelector('.highed-data-wizard-container');

    // TODO: take from datapage
    this.multipleSeriesBlacklist = [
      'candlestick',
      'bubble',
      'tilemap',
      'pie',
      'ohlc',
      'item',
      'dependencywheel',
      'sankey',
      'wordcloud',
      'vector',
      'boxplot'
    ];

    // Dont need the helper for these types
    this.blacklist = ['heatmap', 'timeline', 'packedbubble'];
  }

  resetValues() {
    this.data = null;
    this.mapData = null;
    this.parsedData = null;
    this.seriesPrefix = '';
    this.seriesSuffix = '';
    this.rowsSeries = [];
    this.seriesCount = 0;
    this.seriesLeeway = 2;
    this.assignOptions = {};
  }

  areColumnsAllNumbers(index) {
    let chartData = this.parsedData,
      hasNumber = /\d/;
    const hasDecimalAndThousandSeparator = /(?=.*,)(?=.*\.)/g;

    if (index === 0) return false;

    let allEmpty = true;
    const columnsHaveNumbers = chartData.every((columns, rowIndex) => {
      if (rowIndex === 0) return true;

      const isNumber = hasNumber.test(columns[index]);
      const isEmpty = columns[index] === null || columns[index] === undefined || columns[index] === '';

      if (!isEmpty) allEmpty = false;
      if (isNumber || isEmpty) {
        let stringVersion = (!isNull(columns[index]) ? columns[index] : '').toString();
        const prefixAndSuffix = stringVersion.split(/-?\d+\.?\,?\d*\.?\,?\d*/);

        stringVersion = stringVersion.replace(' ', '');
        if (isNumber && hasDecimalAndThousandSeparator.test(stringVersion)) {
          stringVersion = stringVersion.replace(/,(?=\d{3})/g, '');
        } else stringVersion = stringVersion;

        let numberVal = stringVersion.match(/-?\d+\.?\,?\d*/) || '';
        columns[index] = numberVal[0];

        if (prefixAndSuffix.length > 1) {
          if (this.seriesPrefix === '' && prefixAndSuffix[0] !== '') this.seriesPrefix = prefixAndSuffix[0];
          if (this.seriesSuffix === '' && prefixAndSuffix[1] !== '') this.seriesSuffix = prefixAndSuffix[1];
        }
      }
      return isNumber || isEmpty;
    });

    return !allEmpty && index !== 0 && columnsHaveNumbers;
  }

  // Helper function for wizard (parseWizardData)
  // Basically just a duplicate of the show function without all the dom manipulation
  // TODO Merge the two to remove duplicate code
  parseWizardData(customizedOptions, aggregatedOptions, chartData) {
    return new Promise((resolve, reject) => {
      this.resetValues();
      this.parsedData = chartData;

      if (this.parsedData && this.parsedData.length) {
        this.parsedData = this.parsedData.filter((a) => a.length !== 0);
      }

      let vals = [];
      let count = 0;

      const type = getSeriesType(customizedOptions, 0, aggregatedOptions);

      let options;

      this.assignOptions = merge({}, assignDefaults);
      if (chartTypes[type]) {
        options = merge(merge({}, assignDefaults), merge({}, chartTypes[type]));
        clean(options);
        this.assignOptions = options;
      }

      if (this.blacklist.includes(type)) {
        return resolve({
          csv: this.parsedData
            .map((cols) => {
              return cols.join(';');
            })
            .join('\n'),
          rawData: this.parsedData,
          customizedOptionChanges: {},
          assigns: [
            {
              ...this.assignOptions
            }
          ]
        });
      }

      if (chartTypes[type]) {
        this.selects = [
          {
            name: 'Disabled'
          }
        ];

        let dropdownOptions = [];
        Object.keys(options ?? []).forEach((key) => {
          const opt = options[key];
          if (opt.mandatory || opt.isLabel || opt.isData) {
            dropdownOptions.push({
              name: opt.name,
              value: key,
              order: opt.order,
              colors: generateRandomColors(),
              multiple: opt.multiple
            });
          }
        });

        this.selects = [...this.selects, ...dropdownOptions.sort((a, b) => a.order - b.order)];
      }

      let selectCount = 2; // Skip disabled and categories

      this.parsedData[0].some((element, index) => {
        if (this.areColumnsAllNumbers(index)) {
          if (!vals[count]) {
            vals[count] = cloneDeep(this.assignOptions);
          }

          vals[count] = {
            ...vals[count],
            [this.selects[1] ? this.selects[1].value : 'values']: {
              ...this.assignOptions[this.selects[1] ? this.selects[1].value : 'values'],
              rawValue: [0],
              value: getLetterFromIndex(0)
            }
          };

          vals[count][this.selects[selectCount] ? this.selects[selectCount].value : 'values'] = {
            ...this.assignOptions[this.selects[selectCount] ? this.selects[selectCount].value : 'values'],
            rawValue: [index],
            value: getLetterFromIndex(index)
          };

          if (selectCount < this.selects.length - 1) selectCount++;
          else {
            selectCount = 2;
            count++;
            if (this.multipleSeriesBlacklist.includes(type)) {
              return true;
            }
          }
        }
      });

      const hasPrefix = this.seriesPrefix !== '';
      const hasSuffix = this.seriesSuffix !== '';

      let chartConfigOptions = {
        yAxis: {},
        tooltip: {}
      };

      if (hasPrefix) {
        chartConfigOptions.yAxis = {
          labels: {
            format: this.seriesPrefix + '{value}'
          }
        };
        chartConfigOptions.tooltip = {
          valuePrefix: this.seriesPrefix
        };
      }
      if (hasSuffix) {
        if (!chartConfigOptions.yAxis.labels) chartConfigOptions.yAxis.labels = {};
        if (!chartConfigOptions.tooltip) chartConfigOptions.tooltip = {};
        chartConfigOptions.yAxis.labels.format =
          (chartConfigOptions.yAxis.labels.format || '{value}') + this.seriesSuffix;
        chartConfigOptions.tooltip.valueSuffix = this.seriesSuffix;
      }

      return resolve({
        csv: this.parsedData
          .map((cols) => {
            return cols.join(';');
          })
          .join('\n'),
        rawData: this.parsedData,
        customizedOptionChanges: chartConfigOptions,
        assigns: vals
      });
    });
  }

  on(type, fn) {
    this.events.on(type, fn);
  }
}
